/**
 * C2C Number Selector — Shopify Product Page Widget
 *
 * Renders a player number selector on Shopify product pages.
 * Fetches available numbers from the C2C numbering engine API
 * and adds the selection as line item properties on add-to-cart.
 */
(function () {
  'use strict';

  var container = document.getElementById('c2c-number-selector');
  if (!container) return;

  var API_URL = container.dataset.apiUrl;
  var PRODUCT_ID = container.dataset.productId;
  if (!API_URL || !PRODUCT_ID) return;

  // ── State ──────────────────────────────────────────────────────
  var config = null;
  var selectedNumber = null;
  var selectedDivisionId = null;
  var selectedDivisionName = '';
  var availableNumbers = [];
  var form = null;
  var bundleItemSizes = {}; // { productId: size }
  var includedOptionalItems = {}; // { productId: true/false }

  // ── Init ───────────────────────────────────────────────────────
  init();

  function init() {
    fetch(API_URL + '/storefront/products/' + PRODUCT_ID)
      .then(function (res) {
        if (!res.ok) throw new Error('Not configured');
        return res.json();
      })
      .then(function (data) {
        config = data;
        form = findProductForm();
        render();
        container.style.display = '';
      })
      .catch(function () {
        // Product not in numbering system — hide widget silently
        container.style.display = 'none';
      });
  }

  // ── Render ─────────────────────────────────────────────────────
  function render() {
    container.innerHTML = '';

    // Styles
    injectStyles();

    // Title
    var title = el('div', 'c2c-title', 'Select Your Player Number');
    container.appendChild(title);

    // Player Name
    var nameGroup = createField('Player Name', function () {
      var input = el('input', 'c2c-input');
      input.type = 'text';
      input.id = 'c2c-player-name';
      input.placeholder = 'Enter player name';
      input.required = true;
      input.addEventListener('input', updateFormFields);
      return input;
    });
    container.appendChild(nameGroup);

    // Birth Year
    var birthYearGroup = createField('Birth Year', function () {
      var select = el('select', 'c2c-select');
      select.id = 'c2c-birth-year';
      var opt = document.createElement('option');
      opt.value = '';
      opt.textContent = 'Select birth year...';
      select.appendChild(opt);

      config.birthYears.forEach(function (y) {
        var o = document.createElement('option');
        o.value = y;
        o.textContent = y;
        select.appendChild(o);
      });

      select.addEventListener('change', onSelectionChange);
      return select;
    });
    container.appendChild(birthYearGroup);

    // Sex selector (only if club has gendered divisions)
    if (config.hasGenderedDivisions) {
      var sexGroup = createField('Gender', function () {
        var wrap = el('div', 'c2c-radio-group');
        [
          ['M', 'Male'],
          ['F', 'Female'],
        ].forEach(function (pair) {
          var label = document.createElement('label');
          label.className = 'c2c-radio-label';
          var radio = document.createElement('input');
          radio.type = 'radio';
          radio.name = 'c2c-sex';
          radio.value = pair[0];
          radio.addEventListener('change', onSelectionChange);
          label.appendChild(radio);
          label.appendChild(document.createTextNode(' ' + pair[1]));
          wrap.appendChild(label);
        });
        return wrap;
      });
      container.appendChild(sexGroup);
    }

    // Numbers container (hidden initially) — for non-bundle products, placed here
    // For bundle products, this gets moved inside the bundle section after the carriesNumber item
    var numbersSection = el('div', 'c2c-numbers-section');
    numbersSection.id = 'c2c-numbers-section';
    numbersSection.style.display = 'none';

    var numbersLabel = el('div', 'c2c-field-label', 'Available Numbers');
    numbersSection.appendChild(numbersLabel);

    var loading = el('div', 'c2c-loading', 'Loading available numbers...');
    loading.id = 'c2c-loading';
    loading.style.display = 'none';
    numbersSection.appendChild(loading);

    var grid = el('div', 'c2c-grid');
    grid.id = 'c2c-numbers-grid';
    numbersSection.appendChild(grid);

    var noNumbers = el('div', 'c2c-no-numbers', 'No numbers available for this selection.');
    noNumbers.id = 'c2c-no-numbers';
    noNumbers.style.display = 'none';
    numbersSection.appendChild(noNumbers);

    // Selected number display
    var selectedDisplay = el('div', 'c2c-selected');
    selectedDisplay.id = 'c2c-selected-display';
    selectedDisplay.style.display = 'none';

    // For non-bundle products, add numbers section here (above bundle)
    if (!config.isBundle) {
      container.appendChild(numbersSection);
      container.appendChild(selectedDisplay);
    }

    // Bundle item sizes (only for bundle products)
    if (config.isBundle && config.bundle && config.bundle.items.length > 0) {
      var bundleSection = el('div', 'c2c-bundle-section');
      bundleSection.id = 'c2c-bundle-section';

      var bundleLabel = el('div', 'c2c-field-label', 'Bundle Item Sizes');
      bundleSection.appendChild(bundleLabel);

      var bundleDesc = el('div', 'c2c-bundle-desc', config.bundle.name);
      bundleSection.appendChild(bundleDesc);

      var DEFAULT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL'];

      config.bundle.items
        .slice()
        .sort(function (a, b) { return a.sortOrder - b.sortOrder; })
        .forEach(function (item) {
          var isRequired = item.required !== false;
          var itemGroup = el('div', 'c2c-bundle-item');
          itemGroup.dataset.productId = item.productId;

          // For optional items, add a checkbox
          if (!isRequired) {
            includedOptionalItems[item.productId] = false;
            var checkLabel = document.createElement('label');
            checkLabel.className = 'c2c-bundle-check-label';
            var checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.className = 'c2c-bundle-checkbox';
            checkbox.dataset.productId = item.productId;
            checkLabel.appendChild(checkbox);

            var checkText = document.createTextNode(' ' + item.productName);
            checkLabel.appendChild(checkText);

            var optTag = el('span', 'c2c-optional-tag', '(optional)');
            checkLabel.appendChild(optTag);

            if (item.carriesNumber) {
              var badge = el('span', 'c2c-carries-number', '(# printed)');
              checkLabel.appendChild(badge);
            }
            itemGroup.appendChild(checkLabel);
          } else {
            var itemLabel = el('div', 'c2c-bundle-item-label');
            itemLabel.textContent = item.productName;
            var reqTag = el('span', 'c2c-required-tag', '(required)');
            itemLabel.appendChild(reqTag);
            if (item.carriesNumber) {
              var badge2 = el('span', 'c2c-carries-number', '(# printed)');
              itemLabel.appendChild(badge2);
            }
            itemGroup.appendChild(itemLabel);
          }

          var sizes = (item.sizeVariants && item.sizeVariants.length > 0) ? item.sizeVariants : DEFAULT_SIZES;
          var sizeSelect = el('select', 'c2c-select');
          sizeSelect.dataset.productId = item.productId;
          sizeSelect.dataset.productName = item.productName;
          if (!isRequired) sizeSelect.style.display = 'none'; // hidden until checked

          var defaultOpt = document.createElement('option');
          defaultOpt.value = '';
          defaultOpt.textContent = 'Select size...';
          sizeSelect.appendChild(defaultOpt);

          sizes.forEach(function (s) {
            var o = document.createElement('option');
            o.value = s;
            o.textContent = s;
            sizeSelect.appendChild(o);
          });

          sizeSelect.addEventListener('change', function () {
            bundleItemSizes[item.productId] = sizeSelect.value;
            updateFormFields();
          });

          // Toggle optional item visibility
          if (!isRequired) {
            var cb = itemGroup.querySelector('.c2c-bundle-checkbox');
            cb.addEventListener('change', function () {
              includedOptionalItems[item.productId] = cb.checked;
              sizeSelect.style.display = cb.checked ? '' : 'none';
              if (!cb.checked) {
                sizeSelect.value = '';
                delete bundleItemSizes[item.productId];
              }
              updateFormFields();
            });
          }

          itemGroup.appendChild(sizeSelect);
          bundleSection.appendChild(itemGroup);

          // For the carriesNumber item, insert the number grid right after it
          if (item.carriesNumber) {
            bundleSection.appendChild(numbersSection);
            bundleSection.appendChild(selectedDisplay);
          }
        });

      container.appendChild(bundleSection);
    }

    // Hidden form fields
    if (form) {
      addHiddenInput('properties[Player Number]', 'c2c-prop-number');
      addHiddenInput('properties[Player Name]', 'c2c-prop-name');
      addHiddenInput('properties[Birth Year]', 'c2c-prop-birth-year');
      addHiddenInput('properties[Division]', 'c2c-prop-division');
      addHiddenInput('properties[_divisionId]', 'c2c-prop-division-id');
      if (config.hasGenderedDivisions) {
        addHiddenInput('properties[Sex]', 'c2c-prop-sex');
      }
      if (config.isBundle && config.bundle) {
        addHiddenInput('properties[_bundleId]', 'c2c-prop-bundle-id');
        // Add hidden inputs for each bundle item size
        config.bundle.items.forEach(function (item) {
          addHiddenInput('properties[Size - ' + item.productName + ']', 'c2c-prop-size-' + item.productId);
        });
      }
    }
  }

  // ── Events ─────────────────────────────────────────────────────
  function onSelectionChange() {
    var birthYear = document.getElementById('c2c-birth-year').value;
    if (!birthYear) {
      hideNumbers();
      return;
    }

    var sex = null;
    if (config.hasGenderedDivisions) {
      var checked = document.querySelector('input[name="c2c-sex"]:checked');
      if (!checked) {
        hideNumbers();
        return;
      }
      sex = checked.value;
    }

    // Resolve division from birth year + sex
    var division = resolveDivision(parseInt(birthYear, 10), sex);
    if (!division) {
      hideNumbers();
      showNoNumbers('No division found for this birth year.');
      return;
    }

    selectedDivisionId = division.id;
    selectedDivisionName = division.name;
    selectedNumber = null;
    updateFormFields();

    fetchAvailableNumbers(division.id, parseInt(birthYear, 10));
  }

  function resolveDivision(birthYear, sex) {
    // Find the age group that contains this birth year
    var ageGroup = null;
    for (var i = 0; i < config.ageGroups.length; i++) {
      var ag = config.ageGroups[i];
      if (ag.birthYears.indexOf(birthYear) !== -1) {
        ageGroup = ag;
        break;
      }
    }
    if (!ageGroup) return null;

    // Find division matching age group (and sex if applicable)
    var matching = config.divisions.filter(function (d) {
      return d.ageGroupId === ageGroup.id;
    });

    if (sex) {
      var gendered = matching.filter(function (d) {
        return d.sex === sex;
      });
      if (gendered.length > 0) return gendered[0];
    }

    return matching[0] || null;
  }

  function fetchAvailableNumbers(divisionId, birthYear) {
    var section = document.getElementById('c2c-numbers-section');
    var loading = document.getElementById('c2c-loading');
    var grid = document.getElementById('c2c-numbers-grid');
    var noNumbers = document.getElementById('c2c-no-numbers');

    section.style.display = '';
    loading.style.display = '';
    grid.innerHTML = '';
    noNumbers.style.display = 'none';

    var url =
      API_URL +
      '/storefront/products/' +
      PRODUCT_ID +
      '/available-numbers?divisionId=' +
      divisionId +
      '&birthYear=' +
      birthYear;

    fetch(url)
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        loading.style.display = 'none';
        availableNumbers = data.available || [];

        if (availableNumbers.length === 0) {
          showNoNumbers('All numbers are taken for this group.');
          return;
        }

        renderNumberGrid(availableNumbers);
      })
      .catch(function () {
        loading.style.display = 'none';
        showNoNumbers('Could not load available numbers. Please try again.');
      });
  }

  function renderNumberGrid(numbers) {
    var grid = document.getElementById('c2c-numbers-grid');
    grid.innerHTML = '';

    numbers.forEach(function (num) {
      var btn = el('button', 'c2c-number-btn');
      btn.type = 'button';
      btn.textContent = num;
      btn.dataset.number = num;

      btn.addEventListener('click', function () {
        // Deselect previous
        var prev = grid.querySelector('.c2c-number-btn--selected');
        if (prev) prev.classList.remove('c2c-number-btn--selected');

        // Select this one
        btn.classList.add('c2c-number-btn--selected');
        selectedNumber = num;
        updateFormFields();

        // Show selected display
        var display = document.getElementById('c2c-selected-display');
        display.textContent = 'Selected: #' + num;
        display.style.display = '';
      });

      grid.appendChild(btn);
    });
  }

  function updateFormFields() {
    if (!form) return;

    var playerName = (document.getElementById('c2c-player-name') || {}).value || '';
    var birthYear = (document.getElementById('c2c-birth-year') || {}).value || '';
    var sexRadio = document.querySelector('input[name="c2c-sex"]:checked');

    setHidden('c2c-prop-number', selectedNumber || '');
    setHidden('c2c-prop-name', playerName);
    setHidden('c2c-prop-birth-year', birthYear);
    setHidden('c2c-prop-division', selectedDivisionName);
    setHidden('c2c-prop-division-id', selectedDivisionId || '');
    if (config.hasGenderedDivisions) {
      setHidden('c2c-prop-sex', sexRadio ? sexRadio.value : '');
    }

    // Bundle properties
    var allBundleSizesFilled = true;
    if (config.isBundle && config.bundle) {
      setHidden('c2c-prop-bundle-id', config.bundle.id);
      config.bundle.items.forEach(function (item) {
        var isRequired = item.required !== false;
        var isIncluded = isRequired || !!includedOptionalItems[item.productId];
        var size = isIncluded ? (bundleItemSizes[item.productId] || '') : '';
        setHidden('c2c-prop-size-' + item.productId, size);
        if (isIncluded && !size) allBundleSizesFilled = false;
      });
    }

    // Toggle add-to-cart: require number + name + (all bundle sizes if bundle)
    var valid = selectedNumber && playerName.trim();
    if (config.isBundle && config.bundle) {
      valid = valid && allBundleSizesFilled;
    }
    toggleAddToCart(!!valid);

    // Update carries-number badge with selected number
    if (config.isBundle && selectedNumber) {
      var badges = container.querySelectorAll('.c2c-carries-number');
      badges.forEach(function (badge) {
        badge.textContent = '(#' + selectedNumber + ' printed)';
      });
    }
  }

  // ── Add-to-cart gating ─────────────────────────────────────────
  function toggleAddToCart(enabled) {
    if (!form) return;

    var buttons = form.querySelectorAll(
      '[type="submit"], button[name="add"], .product-form__submit, .shopify-payment-button'
    );

    buttons.forEach(function (btn) {
      if (enabled) {
        btn.removeAttribute('disabled');
        btn.style.opacity = '';
        btn.style.pointerEvents = '';
      } else {
        btn.setAttribute('disabled', 'disabled');
        btn.style.opacity = '0.5';
        btn.style.pointerEvents = 'none';
      }
    });
  }

  // ── Helpers ────────────────────────────────────────────────────
  function hideNumbers() {
    var section = document.getElementById('c2c-numbers-section');
    if (section) section.style.display = 'none';
    var display = document.getElementById('c2c-selected-display');
    if (display) display.style.display = 'none';
    selectedNumber = null;
    updateFormFields();
  }

  function showNoNumbers(message) {
    var noNumbers = document.getElementById('c2c-no-numbers');
    var grid = document.getElementById('c2c-numbers-grid');
    if (grid) grid.innerHTML = '';
    if (noNumbers) {
      noNumbers.textContent = message;
      noNumbers.style.display = '';
    }
  }

  function findProductForm() {
    // Try common selectors across Shopify themes
    return (
      container.closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]') ||
      container.closest('form.product-form') ||
      document.querySelector('form.product-form') ||
      container.closest('product-form form') ||
      document.querySelector('product-form form')
    );
  }

  function addHiddenInput(name, id) {
    if (!form) return;
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.id = id;
    input.value = '';
    form.appendChild(input);
  }

  function setHidden(id, value) {
    var input = document.getElementById(id);
    if (input) input.value = value;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function createField(labelText, buildInput) {
    var group = el('div', 'c2c-field');
    var label = el('div', 'c2c-field-label', labelText);
    group.appendChild(label);
    group.appendChild(buildInput());
    return group;
  }

  // ── Styles ─────────────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('c2c-ns-styles')) return;
    var style = document.createElement('style');
    style.id = 'c2c-ns-styles';
    style.textContent = [
      '#c2c-number-selector {',
      '  border: 1px solid #e5e5e5;',
      '  border-radius: 8px;',
      '  padding: 20px;',
      '  margin: 16px 0;',
      '  font-family: inherit;',
      '}',
      '.c2c-title {',
      '  font-size: 16px;',
      '  font-weight: 600;',
      '  margin-bottom: 16px;',
      '  color: #1a1a1a;',
      '}',
      '.c2c-field {',
      '  margin-bottom: 14px;',
      '}',
      '.c2c-field-label {',
      '  font-size: 13px;',
      '  font-weight: 500;',
      '  margin-bottom: 6px;',
      '  color: #555;',
      '  text-transform: uppercase;',
      '  letter-spacing: 0.5px;',
      '}',
      '.c2c-input, .c2c-select {',
      '  width: 100%;',
      '  padding: 10px 12px;',
      '  border: 1px solid #d1d1d1;',
      '  border-radius: 6px;',
      '  font-size: 14px;',
      '  box-sizing: border-box;',
      '  background: #fff;',
      '  color: #1a1a1a;',
      '}',
      '.c2c-input:focus, .c2c-select:focus {',
      '  outline: none;',
      '  border-color: #1a73e8;',
      '  box-shadow: 0 0 0 2px rgba(26,115,232,0.15);',
      '}',
      '.c2c-radio-group {',
      '  display: flex;',
      '  gap: 16px;',
      '}',
      '.c2c-radio-label {',
      '  display: flex;',
      '  align-items: center;',
      '  gap: 6px;',
      '  font-size: 14px;',
      '  cursor: pointer;',
      '  color: #333;',
      '}',
      '.c2c-numbers-section {',
      '  margin-top: 16px;',
      '}',
      '.c2c-grid {',
      '  display: grid;',
      '  grid-template-columns: repeat(auto-fill, minmax(52px, 1fr));',
      '  gap: 8px;',
      '  margin-top: 8px;',
      '  max-height: 240px;',
      '  overflow-y: auto;',
      '  padding: 2px;',
      '}',
      '.c2c-number-btn {',
      '  display: flex;',
      '  align-items: center;',
      '  justify-content: center;',
      '  height: 44px;',
      '  border: 2px solid #d1d1d1;',
      '  border-radius: 6px;',
      '  background: #fff;',
      '  font-size: 15px;',
      '  font-weight: 600;',
      '  color: #333;',
      '  cursor: pointer;',
      '  transition: all 0.15s ease;',
      '}',
      '.c2c-number-btn:hover {',
      '  border-color: #1a73e8;',
      '  color: #1a73e8;',
      '  background: #f0f6ff;',
      '}',
      '.c2c-number-btn--selected {',
      '  border-color: #1a73e8;',
      '  background: #1a73e8;',
      '  color: #fff;',
      '}',
      '.c2c-number-btn--selected:hover {',
      '  background: #1557b0;',
      '  border-color: #1557b0;',
      '  color: #fff;',
      '}',
      '.c2c-loading {',
      '  font-size: 14px;',
      '  color: #888;',
      '  padding: 12px 0;',
      '}',
      '.c2c-no-numbers {',
      '  font-size: 14px;',
      '  color: #c62828;',
      '  padding: 12px 0;',
      '}',
      '.c2c-selected {',
      '  margin-top: 12px;',
      '  padding: 10px 14px;',
      '  background: #e8f5e9;',
      '  border-radius: 6px;',
      '  font-size: 15px;',
      '  font-weight: 600;',
      '  color: #2e7d32;',
      '}',
      '.c2c-bundle-section {',
      '  margin-top: 16px;',
      '  padding: 14px;',
      '  border: 1px solid #e0e0e0;',
      '  border-radius: 8px;',
      '  background: #fafafa;',
      '}',
      '.c2c-bundle-desc {',
      '  font-size: 14px;',
      '  font-weight: 500;',
      '  color: #333;',
      '  margin-bottom: 12px;',
      '}',
      '.c2c-bundle-item {',
      '  margin-bottom: 10px;',
      '}',
      '.c2c-bundle-item:last-child {',
      '  margin-bottom: 0;',
      '}',
      '.c2c-bundle-item-label {',
      '  font-size: 13px;',
      '  font-weight: 500;',
      '  color: #555;',
      '  margin-bottom: 4px;',
      '}',
      '.c2c-carries-number {',
      '  font-size: 11px;',
      '  font-weight: 400;',
      '  color: #1a73e8;',
      '  margin-left: 6px;',
      '}',
      '.c2c-bundle-check-label {',
      '  display: flex;',
      '  align-items: center;',
      '  gap: 6px;',
      '  font-size: 13px;',
      '  font-weight: 500;',
      '  color: #555;',
      '  cursor: pointer;',
      '  margin-bottom: 4px;',
      '}',
      '.c2c-bundle-checkbox {',
      '  cursor: pointer;',
      '}',
      '.c2c-optional-tag {',
      '  font-size: 11px;',
      '  font-weight: 400;',
      '  color: #888;',
      '  margin-left: 4px;',
      '}',
      '.c2c-required-tag {',
      '  font-size: 11px;',
      '  font-weight: 400;',
      '  color: #888;',
      '  margin-left: 6px;',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }
})();
